import { z } from 'zod';

/** Enveloppe de réponse — identique côté API et côté client. */
export type ApiSuccess<T> = { data: T };
export type ApiFailure = { error: { code: string; message: string; details?: unknown } };
export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export type PageMeta = { page: number; limit: number; total: number };
export type Paginated<T> = { data: T[]; meta: PageMeta };

export const PAGE_DEFAULT = 1;
export const LIMIT_DEFAULT = 25;
export const LIMIT_MAX = 200;

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(PAGE_DEFAULT),
  limit: z.coerce.number().int().min(1).max(LIMIT_MAX).default(LIMIT_DEFAULT),
});
export type Pagination = z.infer<typeof paginationSchema>;

/** Date au format ISO court `YYYY-MM-DD`, tel que stocké en base. */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ');

/**
 * Date ISO courte, ou rien. Un `<input type="date">` vidé renvoie `''`, qu'il
 * faut lire comme « pas de date » plutôt que comme une date invalide.
 */
export const isoDateNullableSchema = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((v, ctx) => {
    if (v === null || v === undefined) return null;
    const t = v.trim();
    if (t === '') return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Date attendue au format AAAA-MM-JJ' });
      return z.NEVER;
    }
    return t;
  });

/** Horodatage ISO complet. */
export const isoDateTimeSchema = z.string().min(10);

/** Chaîne vide -> null. Utilisé partout car l'import Excel produit beaucoup de vides. */
export const nullableText = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((v) => {
    if (v === null || v === undefined) return null;
    const t = v.trim();
    return t === '' ? null : t;
  });

/** Montant en dirhams : accepte "1 250,50", "1250.5", 1250.5. */
export const montantSchema = z
  .union([z.number(), z.string()])
  .transform((v, ctx) => {
    if (typeof v === 'number') return v;
    const cleaned = v.replace(/\s| /g, '').replace(',', '.');
    if (cleaned === '') return 0;
    const n = Number(cleaned);
    if (!Number.isFinite(n)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Montant invalide' });
      return z.NEVER;
    }
    return n;
  });

export const montantNullableSchema = z
  .union([z.number(), z.string(), z.null(), z.undefined()])
  .transform((v, ctx) => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    const cleaned = v.replace(/\s| /g, '').replace(',', '.');
    if (cleaned === '') return null;
    const n = Number(cleaned);
    if (!Number.isFinite(n)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Montant invalide' });
      return z.NEVER;
    }
    return n;
  });

/** Arrondi commercial à 2 décimales, moitié vers le haut (règle 4). */
export function round2(value: number): number {
  if (!Number.isFinite(value)) return 0;
  // Le décalage epsilon évite que 1.005 tombe à 1.00 par erreur de représentation binaire.
  const shifted = value * 100;
  return Math.round(shifted + (shifted >= 0 ? Number.EPSILON * Math.abs(shifted) : -Number.EPSILON * Math.abs(shifted))) / 100;
}

/** Codes d'erreur métier, repris tels quels par le client pour cibler un champ. */
export const ERROR_CODES = {
  VALIDATION: 'VALIDATION',
  INTROUVABLE: 'INTROUVABLE',
  CONFLIT: 'CONFLIT',
  REFERENCE_DUPLIQUEE: 'REFERENCE_DUPLIQUEE',
  STOCK_INSUFFISANT: 'STOCK_INSUFFISANT',
  FACTURE_IMMUABLE: 'FACTURE_IMMUABLE',
  STATUT_INVALIDE: 'STATUT_INVALIDE',
  NON_AUTORISE: 'NON_AUTORISE',
  INTERNE: 'INTERNE',
} as const;
export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];
